import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { addDays, format, parseISO, startOfWeek } from 'date-fns';
import { ClipboardList, Pencil, Plus, Trash2, Users } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';
import { useApp } from '@/contexts/AppContext';
import AppDateField from '@/components/AppDateField';
import { buildHouseholdShareScopeFilter, getHouseholdSharePeerName } from '@/lib/household-share';
import { loadIntegrationSettings, saveIntegrationSettings, type PreferredSupermarket } from '@/lib/integration-settings';
import {
  AppInlineAlert,
  AppButton,
  AppButtonRow,
  AppCard,
  AppEmptyState,
  AppFieldGroup,
  AppInput,
  AppLoadingState,
  AppSectionHeader,
  AppText,
  AppTextArea,
  buildFamilyAsdaBasketPlan,
  buildFamilyFridgeMealSuggestions,
  buildFamilyShoppingList,
  buildFamilyShoppingListFromText,
  buildFamilySupermarketLinks,
  formatFamilyShoppingList,
  getPreferredFamilySupermarketLink,
  movePreferredFamilySupermarketFirst,
  parseFamilyIngredientList,
} from '../../../ui-foundation';
import { AppNativeSelect } from '../../../ui-foundation/adapters/web';

type FamilyMealPlanRow = Database['public']['Tables']['family_meal_plans']['Row'];
type FamilyMealPlanMealRow = Database['public']['Tables']['family_meal_plan_meals']['Row'];

type MealSlot = FamilyMealPlanMealRow['meal_slot'];

const SLOT_OPTIONS: Array<{ value: MealSlot; label: string }> = [
  { value: 'breakfast', label: 'Breakfast' },
  { value: 'lunch', label: 'Lunch' },
  { value: 'dinner', label: 'Dinner' },
  { value: 'snack', label: 'Snack' },
];

const SLOT_ORDER: Record<string, number> = {
  breakfast: 0,
  lunch: 1,
  dinner: 2,
  snack: 3,
};

function notifySectionRefresh() {
  window.dispatchEvent(new CustomEvent('adulting:data-changed'));
}

function weekStartKey(date: Date) {
  return format(startOfWeek(date, { weekStartsOn: 1 }), 'yyyy-MM-dd');
}

function formatMoney(value: number | null) {
  if (value == null) return null;
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    maximumFractionDigits: value % 1 === 0 ? 0 : 2,
  }).format(value);
}

function slotLabel(slot: string) {
  return SLOT_OPTIONS.find((option) => option.value === slot)?.label || 'Meal';
}

export default function FamilyMealPlannerSection() {
  const { user, data } = useApp();
  const [weekStartDate, setWeekStartDate] = useState(() => weekStartKey(new Date()));
  const [plan, setPlan] = useState<FamilyMealPlanRow | null>(null);
  const [meals, setMeals] = useState<FamilyMealPlanMealRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [showPlanForm, setShowPlanForm] = useState(false);
  const [showMealForm, setShowMealForm] = useState(false);
  const [savingPlan, setSavingPlan] = useState(false);
  const [savingMeal, setSavingMeal] = useState(false);
  const [deletingPlan, setDeletingPlan] = useState(false);
  const [deleteMealId, setDeleteMealId] = useState<string | null>(null);
  const [editingMeal, setEditingMeal] = useState<FamilyMealPlanMealRow | null>(null);
  const [householdSize, setHouseholdSize] = useState('1');
  const [weeklyBudget, setWeeklyBudget] = useState('');
  const [planNotes, setPlanNotes] = useState('');
  const [mealDate, setMealDate] = useState(weekStartDate);
  const [mealSlot, setMealSlot] = useState<MealSlot>('dinner');
  const [mealTitle, setMealTitle] = useState('');
  const [servings, setServings] = useState('');
  const [ingredientsText, setIngredientsText] = useState('');
  const [mealNotes, setMealNotes] = useState('');
  const [fridgeItemsText, setFridgeItemsText] = useState('');
  const [fridgePhotoFileName, setFridgePhotoFileName] = useState('');
  const [fridgePhotoPreviewUrl, setFridgePhotoPreviewUrl] = useState<string | null>(null);
  const [preferredSupermarket, setPreferredSupermarket] = useState<PreferredSupermarket | null>(null);
  const [supermarketOrderText, setSupermarketOrderText] = useState('');
  const [supermarketSettingsLoading, setSupermarketSettingsLoading] = useState(false);
  const [supermarketSaving, setSupermarketSaving] = useState(false);
  const previousGeneratedShoppingListRef = useRef('');
  const asdaSessionWindowRef = useRef<Window | null>(null);

  const sharedPartnerName = useMemo(
    () => getHouseholdSharePeerName(data.householdShare, user?.id || null) || data.profile.partnerName.trim() || '',
    [data.householdShare, data.profile.partnerName, user?.id],
  );

  const householdDefault = useMemo(() => (
    Math.max(
      1
      + (sharedPartnerName ? 1 : 0)
      + data.profile.children.filter((child) => child.name.trim().length > 0).length,
      1,
    )
  ), [data.profile.children, sharedPartnerName]);

  const fetchPlan = useCallback(async () => {
    if (!user) {
      setPlan(null);
      setMeals([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const anyClient = supabase as any;
    const householdShareId = plan?.household_share_id || data.householdShare?.id || null;
    const scope = buildHouseholdShareScopeFilter(user.id, householdShareId);

    const { data: planRows, error: planError } = await anyClient
      .from('family_meal_plans')
      .select('*')
      .eq('week_start_date', weekStartDate)
      .or(scope);

    if (planError) {
      console.error(planError);
      toast.error('Could not load the meal planner right now.');
      setPlan(null);
      setMeals([]);
      setLoading(false);
      return;
    }

    const planRow = ((planRows || []) as FamilyMealPlanRow[]).sort((left, right) => {
      const leftShared = householdShareId && left.household_share_id === householdShareId ? 1 : 0;
      const rightShared = householdShareId && right.household_share_id === householdShareId ? 1 : 0;
      if (leftShared !== rightShared) return rightShared - leftShared;
      return new Date(right.created_at).getTime() - new Date(left.created_at).getTime();
    })[0] || null;

    setPlan(planRow);

    if (!planRow) {
      setMeals([]);
      setLoading(false);
      return;
    }

    const { data: mealRows, error: mealError } = await supabase
      .from('family_meal_plan_meals')
      .select('*')
      .eq('meal_plan_id', planRow.id)
      .order('meal_date', { ascending: true })
      .order('created_at', { ascending: true });

    if (mealError) {
      console.error(mealError);
      toast.error('Could not load saved meals right now.');
      setMeals([]);
      setLoading(false);
      return;
    }

    const sortedMeals = ((mealRows || []) as FamilyMealPlanMealRow[]).sort((left, right) => {
      const dateComparison = left.meal_date.localeCompare(right.meal_date);
      if (dateComparison !== 0) return dateComparison;
      return (SLOT_ORDER[left.meal_slot] ?? 99) - (SLOT_ORDER[right.meal_slot] ?? 99);
    });

    setMeals(sortedMeals);
    setLoading(false);
  }, [data.householdShare?.id, user, weekStartDate]);

  useEffect(() => {
    void fetchPlan();
  }, [fetchPlan]);

  useEffect(() => {
    if (plan) {
      setHouseholdSize(String(plan.household_size || householdDefault));
      setWeeklyBudget(plan.weekly_budget != null ? String(plan.weekly_budget) : '');
      setPlanNotes(plan.notes || '');
      setShowPlanForm(false);
      return;
    }

    setHouseholdSize(String(householdDefault));
    setWeeklyBudget('');
    setPlanNotes('');
  }, [householdDefault, plan]);

  const weekStart = useMemo(() => parseISO(weekStartDate), [weekStartDate]);
  const weekEnd = useMemo(() => addDays(weekStart, 6), [weekStart]);
  const weekDays = useMemo(
    () => Array.from({ length: 7 }, (_, index) => addDays(weekStart, index)),
    [weekStart],
  );

  useEffect(() => () => {
    if (fridgePhotoPreviewUrl) {
      URL.revokeObjectURL(fridgePhotoPreviewUrl);
    }
  }, [fridgePhotoPreviewUrl]);

  const shoppingList = useMemo(() => buildFamilyShoppingList(meals), [meals]);
  const shoppingListText = useMemo(() => formatFamilyShoppingList(shoppingList), [shoppingList]);
  const orderReadyShoppingList = useMemo(
    () => buildFamilyShoppingListFromText(supermarketOrderText),
    [supermarketOrderText],
  );
  const supermarketLinks = useMemo(
    () => movePreferredFamilySupermarketFirst(buildFamilySupermarketLinks(orderReadyShoppingList).slice(0, 4), preferredSupermarket),
    [orderReadyShoppingList, preferredSupermarket],
  );
  const preferredSupermarketLink = useMemo(
    () => getPreferredFamilySupermarketLink(supermarketLinks, preferredSupermarket),
    [preferredSupermarket, supermarketLinks],
  );
  const asdaBasketPlan = useMemo(
    () => (preferredSupermarket === 'asda' ? buildFamilyAsdaBasketPlan(orderReadyShoppingList) : null),
    [orderReadyShoppingList, preferredSupermarket],
  );
  const fridgeVisibleItems = useMemo(() => parseFamilyIngredientList(fridgeItemsText), [fridgeItemsText]);
  const fridgeSuggestions = useMemo(
    () => buildFamilyFridgeMealSuggestions(fridgeVisibleItems, plan?.household_size || householdDefault),
    [fridgeVisibleItems, householdDefault, plan?.household_size],
  );
  const plannedDayCount = useMemo(
    () => new Set(meals.map((meal) => meal.meal_date)).size,
    [meals],
  );
  const budgetPerMeal = useMemo(() => {
    if (!plan?.weekly_budget || meals.length === 0) return null;
    return plan.weekly_budget / meals.length;
  }, [meals.length, plan?.weekly_budget]);

  useEffect(() => {
    setSupermarketOrderText((current) => {
      if (!current.trim() || current === previousGeneratedShoppingListRef.current) {
        return shoppingListText;
      }
      return current;
    });
    previousGeneratedShoppingListRef.current = shoppingListText;
  }, [shoppingListText]);

  useEffect(() => {
    let cancelled = false;

    if (!user) {
      setPreferredSupermarket(null);
      setSupermarketSettingsLoading(false);
      return;
    }

    setSupermarketSettingsLoading(true);
    void loadIntegrationSettings(user.id)
      .then((settings) => {
        if (!cancelled) {
          setPreferredSupermarket(settings.preferred_supermarket);
        }
      })
      .catch((error) => {
        console.error(error);
        if (!cancelled) {
          setPreferredSupermarket(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setSupermarketSettingsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [user]);

  const openMealForm = (dateValue = weekStartDate, meal?: FamilyMealPlanMealRow) => {
    if (meal) {
      setEditingMeal(meal);
      setMealDate(meal.meal_date);
      setMealSlot(meal.meal_slot as MealSlot);
      setMealTitle(meal.title);
      setServings(String(meal.servings || plan?.household_size || householdDefault));
      setIngredientsText((meal.ingredients || []).join('\n'));
      setMealNotes(meal.notes || '');
    } else {
      setEditingMeal(null);
      setMealDate(dateValue);
      setMealSlot('dinner');
      setMealTitle('');
      setServings(String(plan?.household_size || householdDefault));
      setIngredientsText('');
      setMealNotes('');
    }
    setShowMealForm(true);
    setDeleteMealId(null);
  };

  const closeMealForm = () => {
    setEditingMeal(null);
    setShowMealForm(false);
    setDeleteMealId(null);
  };

  const handleSavePlan = async () => {
    if (!user) return;
    setSavingPlan(true);

    const nextHouseholdSize = Number(householdSize || String(householdDefault));
    const parsedBudget = weeklyBudget.trim() ? Number(weeklyBudget) : null;
    const householdShareId = data.householdShare?.id || null;

    const payload = {
      user_id: plan?.user_id || user.id,
      week_start_date: weekStartDate,
      household_size: Number.isFinite(nextHouseholdSize) && nextHouseholdSize > 0 ? nextHouseholdSize : householdDefault,
      weekly_budget: parsedBudget != null && Number.isFinite(parsedBudget) ? parsedBudget : null,
      notes: planNotes.trim() || null,
      household_share_id: plan ? (plan.household_share_id || null) : householdShareId,
    };

    const { error } = plan
      ? await supabase.from('family_meal_plans').update(payload).eq('id', plan.id)
      : await supabase.from('family_meal_plans').insert(payload);

    setSavingPlan(false);

    if (error) {
      console.error(error);
      toast.error('Could not save the meal planner right now.');
      return;
    }

    toast.success(plan ? 'Meal planner updated.' : 'Meal planner started.');
    setShowPlanForm(false);
    notifySectionRefresh();
    void fetchPlan();
  };

  const handleDeletePlan = async () => {
    if (!plan) return;
    setDeletingPlan(true);

    const { error } = await supabase
      .from('family_meal_plans')
      .delete()
      .eq('id', plan.id);

    setDeletingPlan(false);

    if (error) {
      console.error(error);
      toast.error('Could not remove this week\'s meal plan right now.');
      return;
    }

    toast.success('Meal planner removed for this week.');
    setShowPlanForm(false);
    notifySectionRefresh();
    void fetchPlan();
  };

  const handleSaveMeal = async () => {
    if (!user || !plan || !mealTitle.trim() || !mealDate) return;
    setSavingMeal(true);

    const nextServings = Number(servings || String(plan.household_size || householdDefault));
    const matchingMeal = !editingMeal
      ? meals.find((meal) => meal.meal_date === mealDate && meal.meal_slot === mealSlot)
      : null;

    const payload = {
      meal_plan_id: plan.id,
      user_id: editingMeal?.user_id || matchingMeal?.user_id || user.id,
      household_share_id: plan.household_share_id || null,
      meal_date: mealDate,
      meal_slot: mealSlot,
      title: mealTitle.trim(),
      servings: Number.isFinite(nextServings) && nextServings > 0 ? nextServings : plan.household_size,
      ingredients: parseFamilyIngredientList(ingredientsText),
      notes: mealNotes.trim() || null,
    };

    const { error } = editingMeal
      ? await supabase.from('family_meal_plan_meals').update(payload).eq('id', editingMeal.id)
      : matchingMeal
        ? await supabase.from('family_meal_plan_meals').update(payload).eq('id', matchingMeal.id)
        : await supabase.from('family_meal_plan_meals').insert(payload);

    setSavingMeal(false);

    if (error) {
      console.error(error);
      toast.error('Could not save that meal right now.');
      return;
    }

    toast.success(editingMeal || matchingMeal ? 'Meal updated.' : 'Meal saved.');
    closeMealForm();
    notifySectionRefresh();
    void fetchPlan();
  };

  const handleDeleteMeal = async (mealId: string) => {
    const { error } = await supabase
      .from('family_meal_plan_meals')
      .delete()
      .eq('id', mealId);

    if (error) {
      console.error(error);
      toast.error('Could not remove that meal right now.');
      return;
    }

    toast.success('Meal removed.');
    if (editingMeal?.id === mealId) {
      closeMealForm();
    }
    setDeleteMealId(null);
    notifySectionRefresh();
    void fetchPlan();
  };

  const copyShoppingList = async () => {
    if (!supermarketOrderText.trim()) return;
    try {
      await navigator.clipboard.writeText(supermarketOrderText);
      toast.success('Shopping list copied.');
    } catch (error) {
      console.error(error);
      toast.error('Could not copy the shopping list right now.');
    }
  };

  const handleSelectFridgePhoto = (file: File | null) => {
    if (fridgePhotoPreviewUrl) {
      URL.revokeObjectURL(fridgePhotoPreviewUrl);
    }

    if (!file) {
      setFridgePhotoFileName('');
      setFridgePhotoPreviewUrl(null);
      return;
    }

    setFridgePhotoFileName(file.name);
    setFridgePhotoPreviewUrl(URL.createObjectURL(file));
  };

  const applyFridgeSuggestion = (suggestion: ReturnType<typeof buildFamilyFridgeMealSuggestions>[number]) => {
    const todayKey = format(new Date(), 'yyyy-MM-dd');
    const suggestedDate = todayKey >= weekStartDate && todayKey <= format(weekEnd, 'yyyy-MM-dd')
      ? todayKey
      : weekStartDate;

    setEditingMeal(null);
    setMealDate(suggestedDate);
    setMealSlot(suggestion.slot as MealSlot);
    setMealTitle(suggestion.title);
    setServings(String(plan?.household_size || householdDefault));
    setIngredientsText(suggestion.ingredients.join('\n'));
    setMealNotes(suggestion.note);
    setShowMealForm(true);
    toast.success('Fridge suggestion copied into the meal form.');
  };

  const openSupermarketOrder = async (url: string, label: string) => {
    const provider = supermarketLinks.find((link) => link.url === url && link.label === label) || preferredSupermarketLink;

    try {
      if (supermarketOrderText.trim()) {
        await navigator.clipboard.writeText(supermarketOrderText);
      }
      window.open(url, '_blank', 'noopener,noreferrer');
      toast.success(
        provider?.handoffBody
          || `${label} opened. Your order-ready list is copied for quick paste.`,
      );
    } catch (error) {
      console.error(error);
      window.open(url, '_blank', 'noopener,noreferrer');
      toast.success(`${label} opened. The order-ready list is still here if you want to paste it manually.`);
    }
  };

  const openAsdaSessionWindow = (url: string) => {
    if (asdaSessionWindowRef.current && !asdaSessionWindowRef.current.closed) {
      asdaSessionWindowRef.current.location.href = url;
      asdaSessionWindowRef.current.focus();
      return true;
    }

    const nextWindow = window.open(url, 'adulting-asda-basket');
    if (!nextWindow) {
      return false;
    }

    asdaSessionWindowRef.current = nextWindow;
    nextWindow.focus();
    return true;
  };

  const openAsdaBasketSession = async () => {
    const firstStep = asdaBasketPlan?.steps[0];
    if (!firstStep) return;

    try {
      if (supermarketOrderText.trim()) {
        await navigator.clipboard.writeText(supermarketOrderText);
      }
    } catch (error) {
      console.error(error);
    }

    const opened = openAsdaSessionWindow(firstStep.url);
    if (opened) {
      toast.success('Asda session opened. Sign in once there and keep adding items into the same live basket.');
      return;
    }

    toast.error('Allow the Asda pop-up window to open so Adulting can keep one signed-in basket session alive.');
  };

  const openAsdaBasketStep = async (url: string, label: string) => {
    try {
      await navigator.clipboard.writeText(label);
      const opened = openAsdaSessionWindow(url);
      if (opened) {
        toast.success(`Asda session moved to ${label}. That line is copied too if you want to paste it exactly.`);
        return;
      }
      window.open(url, '_blank', 'noopener,noreferrer');
      toast.success(`Asda opened for ${label}. That line is copied too if you want to paste it exactly.`);
    } catch (error) {
      console.error(error);
      window.open(url, '_blank', 'noopener,noreferrer');
      toast.success(`Asda opened for ${label}.`);
    }
  };

  const handlePreferredSupermarketChange = async (value: string) => {
    const nextValue = (value || null) as PreferredSupermarket | null;
    const previousValue = preferredSupermarket;
    setPreferredSupermarket(nextValue);

    if (!user) {
      return;
    }

    setSupermarketSaving(true);
    try {
      await saveIntegrationSettings(user.id, { preferred_supermarket: nextValue });
      toast.success(
        nextValue
          ? `${nextValue.charAt(0).toUpperCase()}${nextValue.slice(1)} saved as your preferred supermarket.`
          : 'Preferred supermarket cleared.',
      );
    } catch (error) {
      console.error(error);
      setPreferredSupermarket(previousValue);
      toast.error('Could not save that supermarket preference right now.');
    } finally {
      setSupermarketSaving(false);
    }
  };

  if (loading) {
    return (
      <AppLoadingState
        title="Loading meal planner"
        body="Pulling this week's meals, shopping list, and household setup."
      />
    );
  }

  return (
    <AppCard>
      <AppSectionHeader
        title="Meal planner"
        subtitle="Plan one week at a time here in Home, then let Adulting turn the saved ingredients into a shopping list."
        action={(
          <AppText fontSize="$xs" color="$colorMuted">
            Week of {format(weekStart, 'd MMM')} to {format(weekEnd, 'd MMM')}
          </AppText>
        )}
      />

      <AppButtonRow mt="$md">
        <AppButton
          size="sm"
          tone="outline"
          onPress={() => setWeekStartDate(weekStartKey(addDays(weekStart, -7)))}
        >
          Previous week
        </AppButton>
        <AppButton
          size="sm"
          tone="outline"
          onPress={() => setWeekStartDate(weekStartKey(addDays(weekStart, 7)))}
        >
          Next week
        </AppButton>
        <AppButton
          size="sm"
          tone="outline"
          onPress={() => setShowPlanForm((current) => !current)}
        >
          {plan ? 'Edit week setup' : 'Start this week'}
        </AppButton>
        <AppButton
          size="sm"
          onPress={() => openMealForm()}
          disabled={!plan}
        >
          <Plus size={12} />
          Add meal
        </AppButton>
      </AppButtonRow>

      {showPlanForm ? (
        <AppCard backgroundColor="$background" className="mt-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <AppFieldGroup label="Household size">
              <AppInput
                tag="input"
                type="number"
                min={1}
                max={20}
                value={householdSize}
                onChange={(event) => setHouseholdSize((event.target as HTMLInputElement).value)}
              />
            </AppFieldGroup>
            <AppFieldGroup label="Weekly food budget (£)">
              <AppInput
                tag="input"
                type="number"
                min={0}
                step="0.01"
                value={weeklyBudget}
                onChange={(event) => setWeeklyBudget((event.target as HTMLInputElement).value)}
                placeholder="Optional"
              />
            </AppFieldGroup>
          </div>

          <AppFieldGroup label="Weekly note">
            <AppTextArea
              value={planNotes}
              onChange={(event) => setPlanNotes((event.target as HTMLTextAreaElement).value)}
              placeholder="Use this for freezer meals, tight evenings, or anything that affects this week's plan."
              rows={3}
            />
          </AppFieldGroup>

          <AppButtonRow>
            <AppButton
              size="sm"
              onPress={() => void handleSavePlan()}
              disabled={savingPlan}
            >
              {savingPlan ? 'Saving…' : plan ? 'Save week setup' : 'Start meal plan'}
            </AppButton>
            {plan ? (
              <AppButton
                size="sm"
                tone="outline"
                onPress={() => void handleDeletePlan()}
                disabled={deletingPlan}
              >
                {deletingPlan ? 'Removing…' : 'Remove week'}
              </AppButton>
            ) : null}
          </AppButtonRow>
        </AppCard>
      ) : null}

      {!plan ? (
        <AppEmptyState
          title="No meal plan saved for this week yet"
          body="Start the week with the household size and food budget, then save meals as they become clear instead of trying to map every dinner perfectly up front."
          action={(
            <AppButton size="sm" onPress={() => setShowPlanForm(true)}>
              Start this week
            </AppButton>
          )}
        />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-2 mt-4 sm:grid-cols-4">
            <MiniMetric label="Household" value={String(plan.household_size)} icon={<Users size={12} />} />
            <MiniMetric label="Meals" value={String(meals.length)} icon={<ClipboardList size={12} />} />
            <MiniMetric label="Days planned" value={String(plannedDayCount)} icon={<Pencil size={12} />} />
            <MiniMetric label="Budget" value={formatMoney(plan.weekly_budget) || 'Open'} icon={<span className="text-xs">£</span>} />
          </div>

          <AppCard backgroundColor="$background" className="mt-4">
            <AppText fontSize="$sm" fontWeight="700">
              {plan.weekly_budget != null
                ? `${formatMoney(plan.weekly_budget)} budget for the week`
                : 'No weekly budget saved yet'}
            </AppText>
            <AppText className="mt-1" fontSize="$sm" color="$colorMuted">
              Household size set to {plan.household_size}. {budgetPerMeal != null
                ? `That is roughly ${formatMoney(budgetPerMeal)} per saved meal if this week stays as planned.`
                : 'Adulting will show the rough budget-per-meal once at least one meal is saved.'}
            </AppText>
            {plan.notes ? (
              <AppText fontSize="$sm" color="$colorMuted">{plan.notes}</AppText>
            ) : null}
          </AppCard>

          <div className="grid gap-3 mt-4 lg:grid-cols-2">
            {weekDays.map((day) => {
              const dayKey = format(day, 'yyyy-MM-dd');
              const mealsForDay = meals.filter((meal) => meal.meal_date === dayKey);

              return (
                <AppCard key={dayKey} backgroundColor="$background">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <AppText fontSize="$sm" fontWeight="700">{format(day, 'EEEE')}</AppText>
                      <AppText className="mt-1" fontSize="$xs" color="$colorMuted">{format(day, 'd MMM')}</AppText>
                    </div>
                    <AppButton
                      size="sm"
                      tone="outline"
                      onPress={() => openMealForm(dayKey)}
                    >
                      Add
                    </AppButton>
                  </div>

                  {mealsForDay.length === 0 ? (
                    <AppEmptyState
                      title="No meals saved for this day yet"
                      body="Add the meals you know first. The rest of the week can stay flexible."
                    />
                  ) : (
                    <div className="space-y-3 mt-3">
                      {mealsForDay.map((meal) => (
                        <AppCard key={meal.id} backgroundColor="$cardBackground">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="rounded-full bg-plum-light px-2 py-0.5 text-[10px] font-medium text-primary">
                                  {slotLabel(meal.meal_slot)}
                                </span>
                                <AppText fontSize="$sm" fontWeight="700">{meal.title}</AppText>
                              </div>
                              <AppText className="mt-1" fontSize="$xs" color="$colorMuted">
                                Serves {meal.servings}
                                {meal.ingredients.length ? ` · ${meal.ingredients.length} shopping item${meal.ingredients.length === 1 ? '' : 's'}` : ''}
                              </AppText>
                              {meal.ingredients.length ? (
                                <AppText className="mt-2" fontSize="$sm" color="$colorMuted">
                                  {meal.ingredients.join(', ')}
                                </AppText>
                              ) : null}
                              {meal.notes ? (
                                <AppText className="mt-2" fontSize="$sm" color="$colorMuted">{meal.notes}</AppText>
                              ) : null}
                            </div>
                          </div>
                          <AppButtonRow mt="$sm">
                            <AppButton
                              size="sm"
                              tone="outline"
                              onPress={() => openMealForm(meal.meal_date, meal)}
                            >
                              Edit meal
                            </AppButton>
                            {deleteMealId === meal.id ? (
                              <>
                                <AppButton
                                  size="sm"
                                  tone="outline"
                                  onPress={() => void handleDeleteMeal(meal.id)}
                                >
                                  Confirm remove
                                </AppButton>
                                <AppButton
                                  size="sm"
                                  tone="outline"
                                  onPress={() => setDeleteMealId(null)}
                                >
                                  Cancel
                                </AppButton>
                              </>
                            ) : (
                              <AppButton
                                size="sm"
                                tone="outline"
                                onPress={() => setDeleteMealId(meal.id)}
                              >
                                <Trash2 size={12} />
                                Remove
                              </AppButton>
                            )}
                          </AppButtonRow>
                        </AppCard>
                      ))}
                    </div>
                  )}
                </AppCard>
              );
            })}
          </div>
        </>
      )}

      {showMealForm && plan ? (
        <AppCard backgroundColor="$background" className="mt-4">
          <div>
            <AppText fontSize="$sm" fontWeight="700">
              {editingMeal ? 'Edit meal' : 'Add meal'}
            </AppText>
            <AppText className="mt-1" fontSize="$sm" color="$colorMuted">
              Save the meal title, servings, and the ingredients you need so Adulting can keep the shopping list honest.
            </AppText>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <AppDateField
              label="Day"
              value={mealDate}
              onChange={setMealDate}
              minimum={weekStartDate}
              maximum={format(weekEnd, 'yyyy-MM-dd')}
            />
            <AppFieldGroup label="Meal slot">
              <AppNativeSelect
                value={mealSlot}
                onChange={(event) => setMealSlot(event.target.value as MealSlot)}
              >
                {SLOT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                  ))}
                </AppNativeSelect>
            </AppFieldGroup>
          </div>

          <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_140px]">
            <AppFieldGroup label="Meal">
              <AppInput
                tag="input"
                value={mealTitle}
                onChange={(event) => setMealTitle((event.target as HTMLInputElement).value)}
                placeholder="Pasta bake, packed lunch, slow cooker chilli…"
              />
            </AppFieldGroup>
            <AppFieldGroup label="Servings">
              <AppInput
                tag="input"
                type="number"
                min={1}
                max={20}
                value={servings}
                onChange={(event) => setServings((event.target as HTMLInputElement).value)}
              />
            </AppFieldGroup>
          </div>

          <AppFieldGroup label="Ingredients">
            <AppTextArea
              value={ingredientsText}
              onChange={(event) => setIngredientsText((event.target as HTMLTextAreaElement).value)}
              placeholder={'One item per line or comma.\nPasta\nPassata\nMozzarella'}
              rows={5}
            />
          </AppFieldGroup>

          <AppFieldGroup label="Meal note">
            <AppTextArea
              value={mealNotes}
              onChange={(event) => setMealNotes((event.target as HTMLTextAreaElement).value)}
              placeholder="Use this for prep notes, freezer reminders, or the evening when it needs to happen fast."
              rows={3}
            />
          </AppFieldGroup>

          <AppButtonRow>
            <AppButton
              size="sm"
              onPress={() => void handleSaveMeal()}
              disabled={!mealTitle.trim() || !mealDate || savingMeal}
            >
              {savingMeal ? 'Saving…' : editingMeal ? 'Save meal' : 'Add meal'}
            </AppButton>
            <AppButton
              size="sm"
              tone="outline"
              onPress={closeMealForm}
            >
              Cancel
            </AppButton>
          </AppButtonRow>
        </AppCard>
      ) : null}

      {plan ? (
        <AppCard backgroundColor="$background" className="mt-4">
          <AppSectionHeader
            title="Fridge quick scan"
            subtitle="Add a fridge photo if it helps, jot the visible bits you want to use up, and Adulting will turn them into a few calm meal starters."
          />

          <AppFieldGroup label="Fridge photo">
            <AppInput
              tag="input"
              type="file"
              accept="image/*"
              onChange={(event) => handleSelectFridgePhoto((event.target as HTMLInputElement).files?.[0] || null)}
            />
            <AppText className="mt-2" fontSize="$xs" color="$colorMuted">
              Adulting keeps this photo on the device for this quick scan only. It does not read the image automatically yet, so list the visible items below.
            </AppText>
          </AppFieldGroup>

          {fridgePhotoPreviewUrl ? (
            <AppCard backgroundColor="$cardBackground">
              <img
                src={fridgePhotoPreviewUrl}
                alt="Selected fridge photo"
                className="h-48 w-full rounded-2xl object-cover"
              />
              <AppText className="mt-3" fontSize="$xs" color="$colorMuted">
                {fridgePhotoFileName}
              </AppText>
              <AppButtonRow mt="$sm">
                <AppButton size="sm" tone="outline" onPress={() => handleSelectFridgePhoto(null)}>
                  Remove photo
                </AppButton>
              </AppButtonRow>
            </AppCard>
          ) : null}

          <AppFieldGroup label="What can you see?">
            <AppTextArea
              value={fridgeItemsText}
              onChange={(event) => setFridgeItemsText((event.target as HTMLTextAreaElement).value)}
              placeholder={'List the visible bits you want to use up.\nEggs\nSpinach\nCheddar\nPeppers'}
              rows={5}
            />
          </AppFieldGroup>

          {fridgeSuggestions.length === 0 ? (
            <AppEmptyState
              title="No fridge suggestions yet"
              body={fridgePhotoPreviewUrl
                ? 'List the visible items from the photo and Adulting will suggest a few easy meals.'
                : 'Add the visible items you want to use up and Adulting will suggest a few easy meals.'}
            />
          ) : (
            <div className="grid gap-3 mt-3 lg:grid-cols-2">
              {fridgeSuggestions.map((suggestion) => (
                <AppCard key={suggestion.key} backgroundColor="$cardBackground">
                  <AppText fontSize="$sm" fontWeight="700">{suggestion.title}</AppText>
                  <AppText className="mt-1" fontSize="$sm" color="$colorMuted">{suggestion.summary}</AppText>
                  <AppText className="mt-2" fontSize="$xs" color="$colorMuted">
                    {slotLabel(suggestion.slot)}
                    {suggestion.shoppingTopUps.length ? ` · Top up: ${suggestion.shoppingTopUps.join(', ')}` : ''}
                  </AppText>
                  <AppText className="mt-2" fontSize="$sm" color="$colorMuted">{suggestion.note}</AppText>
                  <AppButtonRow mt="$sm">
                    <AppButton size="sm" onPress={() => applyFridgeSuggestion(suggestion)}>
                      Use for meal plan
                    </AppButton>
                  </AppButtonRow>
                </AppCard>
              ))}
            </div>
          )}
        </AppCard>
      ) : null}

      <AppCard backgroundColor="$background" className="mt-4">
        <AppSectionHeader
          title="Generated shopping list"
          subtitle="Adulting builds this from the saved ingredients in the current week. Keep quantities in the ingredient text if you need them exactly as written."
          action={(
            <AppButton
              size="sm"
              tone="outline"
              onPress={() => void copyShoppingList()}
              disabled={!supermarketOrderText.trim()}
            >
              <ClipboardList size={12} />
              Copy list
            </AppButton>
          )}
        />

        {shoppingList.length > 0 ? (
          <>
            <AppFieldGroup label="Preferred supermarket">
              <AppNativeSelect
                value={preferredSupermarket || ''}
                onChange={(event) => void handlePreferredSupermarketChange(event.target.value)}
                disabled={supermarketSettingsLoading || supermarketSaving}
              >
                <option value="">No preference</option>
                <option value="tesco">Tesco</option>
                <option value="sainsburys">Sainsbury&apos;s</option>
                <option value="asda">Asda</option>
                <option value="ocado">Ocado</option>
                <option value="waitrose">Waitrose</option>
                <option value="morrisons">Morrisons</option>
              </AppNativeSelect>
            </AppFieldGroup>
            <AppFieldGroup label="Order-ready list">
              <AppTextArea
                value={supermarketOrderText}
                onChange={(event) => setSupermarketOrderText((event.target as HTMLTextAreaElement).value)}
                placeholder="Adjust wording, quantities, or grouped items before you copy or open a supermarket."
                rows={6}
              />
            </AppFieldGroup>
            <AppText className="mt-3" fontSize="$xs" color="$colorMuted">
              Adulting can copy the full order-ready list and open your chosen supermarket with the strongest items already in search. Keep the text editable here so the basket wording still matches what you actually want to buy.
            </AppText>
            <AppButtonRow mt="$sm">
              {preferredSupermarketLink ? (
                <AppButton
                  size="sm"
                  onPress={() => void openSupermarketOrder(preferredSupermarketLink.url, preferredSupermarketLink.label)}
                >
                  {preferredSupermarketLink.label}
                </AppButton>
              ) : null}
              {supermarketLinks.map((provider) => (
                <AppButton
                  key={provider.id}
                  size="sm"
                  tone="outline"
                  disabled={provider.id === preferredSupermarketLink?.id}
                  onPress={() => void openSupermarketOrder(provider.url, provider.label)}
                >
                  {provider.label}
                </AppButton>
              ))}
            </AppButtonRow>
            {asdaBasketPlan ? (
              <AppCard backgroundColor="$background" className="mt-3">
                <AppSectionHeader
                  title={asdaBasketPlan.title}
                  subtitle="Asda first: keep one signed-in Asda window open, then move through the item searches while the same live basket stays in place."
                />
                <AppInlineAlert title="What this does" tone="info">
                  <AppText fontSize="$sm">{asdaBasketPlan.body}</AppText>
                </AppInlineAlert>
                <AppButtonRow mt="$sm">
                  <AppButton size="sm" onPress={() => void openAsdaBasketSession()}>
                    Open signed-in Asda session
                  </AppButton>
                </AppButtonRow>
                <div className="grid gap-2 sm:grid-cols-2">
                  {asdaBasketPlan.steps.map((step) => (
                    <AppCard key={step.key} backgroundColor="$cardBackground">
                      <AppText fontSize="$sm" fontWeight="700">{step.label}</AppText>
                      <AppText fontSize="$xs" color="$colorMuted">
                        Loads Asda search for {step.searchLabel.toLowerCase()} inside the same basket session.
                      </AppText>
                      <AppButton
                        size="sm"
                        tone="outline"
                        onPress={() => void openAsdaBasketStep(step.url, step.label)}
                      >
                        Load in Asda session
                      </AppButton>
                    </AppCard>
                  ))}
                </div>
              </AppCard>
            ) : null}
          </>
        ) : null}

        {shoppingList.length === 0 ? (
          <AppEmptyState
            title="No shopping list yet"
            body="Add ingredients to meals and Adulting will build it here."
          />
        ) : (
          <div className="grid gap-2 mt-3 sm:grid-cols-2">
            {shoppingList.map((item) => (
              <AppCard key={item.label.toLowerCase()} backgroundColor="$cardBackground">
                <AppText fontSize="$sm" fontWeight="700">{item.label}</AppText>
                <AppText className="mt-1" fontSize="$xs" color="$colorMuted">
                  {item.count > 1 ? `Needed in ${item.count} meal${item.count === 1 ? '' : 's'}` : 'Needed once'}
                </AppText>
                <AppText className="mt-2" fontSize="$xs" color="$colorMuted">
                  {item.usedBy.slice(0, 3).join(' • ')}
                </AppText>
              </AppCard>
            ))}
          </div>
        )}
      </AppCard>
    </AppCard>
  );
}

function MiniMetric({
  label,
  value,
  icon,
}: {
  label: string;
  value: string;
  icon: ReactNode;
}) {
  return (
    <AppCard px="$sm" py="$sm" backgroundColor="$background">
      <div className="flex items-center gap-1.5">
        {icon}
        <AppText fontSize="$xs" fontWeight="700" textTransform="uppercase" letterSpacing={0.8} color="$colorMuted">
          {label}
        </AppText>
      </div>
      <AppText className="mt-1" fontSize="$xl" fontWeight="700">
        {value}
      </AppText>
    </AppCard>
  );
}
